import { useState, useEffect, useCallback, useRef } from 'react';
import {
    collection, query, orderBy, limit, onSnapshot,
    doc, setDoc, updateDoc,
    startAfter, getDocs, deleteDoc
} from 'firebase/firestore';
import { db as firestore } from '../firebaseConfig';
import { GroupChatMessage } from '../types/chat';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { hasPermission } from '../utils/permissions';
import { generateId } from '../utils/helpers';

const MESSAGES_PER_PAGE = 80;

export function useGroupChat(groupId: string | undefined) {
    const { user, userData } = useAuth();
    const { currentGroup, db } = useAppContext();
    const [messages, setMessages] = useState<GroupChatMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);
    const oldestTimestampRef = useRef<string | null>(null);
    const latestTimestampRef = useRef<string | null>(null);
    const isFirstLoad = useRef(true);

    // Determine admin status
    const isAdmin = (() => {
        if (!user || !currentGroup) return false;
        return hasPermission(user, 'manage_group_settings', { group: currentGroup, globalConfig: db.globalConfig }) ||
            hasPermission(user, 'manage_group_members', { group: currentGroup, globalConfig: db.globalConfig });
    })();

    // Chat locked check
    const isChatLocked = currentGroup?.chatSettings?.chatLocked === true;
    const canSendMessage = !isChatLocked || isAdmin;

    // ---- LOCAL STORAGE KEYS ----
    const getLastReadKey = useCallback(() => `padel_chat_lastRead_${groupId}_${user?.uid}`, [groupId, user?.uid]);

    // ---- MARK AS READ ----
    const markAsRead = useCallback(() => {
        if (!groupId || !user) return;
        const now = new Date().toISOString();
        // Use the highest known timestamp to handle clock skew where sender timestamp is slightly in the future
        let timeToSet = now;
        if (latestTimestampRef.current && latestTimestampRef.current > now) {
            timeToSet = latestTimestampRef.current;
        }
        localStorage.setItem(getLastReadKey(), timeToSet);
        setUnreadCount(0);
        // Fire custom event to immediately notify global listeners (like Navbar)
        window.dispatchEvent(new Event('chat_marked_read'));
    }, [groupId, user, getLastReadKey]);

    // ---- UNREAD COUNT (fast, from localStorage) ----
    const computeUnreadCount = useCallback((msgs: GroupChatMessage[]) => {
        if (!user) return 0;
        const lastRead = localStorage.getItem(getLastReadKey());
        if (!lastRead) return msgs.filter(m => m.senderId !== user.uid).length;
        return msgs.filter(m => m.senderId !== user.uid && m.timestamp > lastRead).length;
    }, [user, getLastReadKey]);

    // ---- REAL-TIME LISTENER ----
    useEffect(() => {
        if (!groupId) {
            setMessages([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        isFirstLoad.current = true;

        const messagesRef = collection(firestore, 'groups', groupId, 'chatMessages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(MESSAGES_PER_PAGE));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs: GroupChatMessage[] = [];
            snapshot.forEach(docSnap => {
                msgs.push({ id: docSnap.id, ...docSnap.data() } as GroupChatMessage);
            });

            // Reverse so oldest is first for display
            msgs.reverse();

            // Auto-unpin expired pinned messages (client-side filter)
            const now = new Date().toISOString();
            const processed = msgs.map(m => {
                if (m.isPinned && m.pinnedUntil && m.pinnedUntil < now) {
                    return { ...m, isPinned: false };
                }
                return m;
            });

            setMessages(processed);

            if (processed.length > 0) {
                const latest = processed[processed.length - 1].timestamp;
                if (!latestTimestampRef.current || latest > latestTimestampRef.current) {
                    latestTimestampRef.current = latest;
                }
            }

            if (isFirstLoad.current && processed.length > 0) {
                oldestTimestampRef.current = processed[0].timestamp;
                setHasMore(processed.length >= MESSAGES_PER_PAGE);
                isFirstLoad.current = false;
            }

            // Update unread
            setUnreadCount(computeUnreadCount(processed));
            setLoading(false);
        }, (error) => {
            console.error(`[useGroupChat] Listener error for group ${groupId}:`, error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [groupId, computeUnreadCount]);

    // ---- LOAD OLDER MESSAGES ----
    const loadMore = useCallback(async () => {
        if (!groupId || !oldestTimestampRef.current || loadingMore || !hasMore) return;
        setLoadingMore(true);

        try {
            const messagesRef = collection(firestore, 'groups', groupId, 'chatMessages');
            const q = query(
                messagesRef,
                orderBy('timestamp', 'desc'),
                startAfter(oldestTimestampRef.current),
                limit(MESSAGES_PER_PAGE)
            );

            const snapshot = await getDocs(q);
            const older: GroupChatMessage[] = [];
            snapshot.forEach(docSnap => {
                older.push({ id: docSnap.id, ...docSnap.data() } as GroupChatMessage);
            });

            older.reverse();

            if (older.length > 0) {
                oldestTimestampRef.current = older[0].timestamp;
                setMessages(prev => [...older, ...prev]);
            }

            setHasMore(older.length >= MESSAGES_PER_PAGE);
        } catch (err) {
            console.error('[useGroupChat] loadMore error:', err);
        } finally {
            setLoadingMore(false);
        }
    }, [groupId, loadingMore, hasMore]);

    // ---- SEND MESSAGE ----
    const sendMessage = useCallback(async (
        text: string,
        textColor?: string,
        replyTo?: { id: string; senderName: string; text: string }
    ) => {
        if (!groupId || !user || !text.trim()) return;
        if (!canSendMessage) return;

        const currentPlayer = currentGroup?.players?.find(p => p.id === user.uid);
        const msgId = generateId('chat');
        const senderRole = currentPlayer?.groupRole || currentPlayer?.appRole || 'player';

        const message: GroupChatMessage = {
            id: msgId,
            senderId: user.uid,
            senderName: currentPlayer?.pseudo || currentPlayer?.name || userData?.pseudo || user.displayName || 'Joueur',
            senderAvatar: currentPlayer?.avatar || userData?.photoURL || userData?.avatar || '',
            senderRole,
            text: text.trim(),
            timestamp: new Date().toISOString(),
        };

        if (textColor) message.textColor = textColor;
        if (replyTo) message.replyTo = {
            id: replyTo.id,
            senderName: replyTo.senderName,
            text: replyTo.text.substring(0, 100)
        };

        // Optimistic update
        setMessages(prev => [...prev, message]);

        try {
            const msgRef = doc(firestore, 'groups', groupId, 'chatMessages', msgId);
            await setDoc(msgRef, message);
        } catch (error) {
            console.error('[useGroupChat] sendMessage error:', error);
            // Rollback optimistic
            setMessages(prev => prev.filter(m => m.id !== msgId));
            throw error;
        }
    }, [groupId, user, userData, currentGroup, canSendMessage]);

    // ---- MODERATE (SOFT DELETE FOR EVERYONE) ----
    const moderateMessage = useCallback(async (messageId: string) => {
        if (!groupId || !user || !isAdmin) return;

        try {
            const msgRef = doc(firestore, 'groups', groupId, 'chatMessages', messageId);
            await updateDoc(msgRef, {
                isModerated: true,
                moderatedBy: user.uid,
                moderatedAt: new Date().toISOString(),
                text: '', // Clear text
            });
        } catch (error) {
            console.error('[useGroupChat] moderateMessage error:', error);
            throw error;
        }
    }, [groupId, user, isAdmin]);

    // ---- DELETE MESSAGE (HARD DELETE) ----
    const deleteMessage = useCallback(async (messageId: string) => {
        if (!groupId || !user) return;
        
        try {
            const msgRef = doc(firestore, 'groups', groupId, 'chatMessages', messageId);
            await deleteDoc(msgRef);
        } catch (error) {
            console.error('[useGroupChat] deleteMessage error:', error);
            throw error;
        }
    }, [groupId, user]);

    // ---- PIN MESSAGE ----
    const pinMessage = useCallback(async (messageId: string, duration: '1d' | '7d' | '1m') => {
        if (!groupId || !user || !isAdmin) return;

        const now = new Date();
        let pinnedUntil: Date;
        switch (duration) {
            case '1d': pinnedUntil = new Date(now.getTime() + 24 * 60 * 60 * 1000); break;
            case '7d': pinnedUntil = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); break;
            case '1m': pinnedUntil = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); break;
        }

        const currentPlayer = currentGroup?.players?.find(p => p.id === user.uid);

        try {
            const msgRef = doc(firestore, 'groups', groupId, 'chatMessages', messageId);
            await updateDoc(msgRef, {
                isPinned: true,
                pinnedUntil: pinnedUntil.toISOString(),
                pinnedBy: user.uid,
                pinnedByName: currentPlayer?.pseudo || currentPlayer?.name || user.displayName || 'Admin',
            });
        } catch (error) {
            console.error('[useGroupChat] pinMessage error:', error);
            throw error;
        }
    }, [groupId, user, isAdmin, currentGroup]);

    // ---- UNPIN MESSAGE ----
    const unpinMessage = useCallback(async (messageId: string) => {
        if (!groupId || !user || !isAdmin) return;

        try {
            const msgRef = doc(firestore, 'groups', groupId, 'chatMessages', messageId);
            await updateDoc(msgRef, {
                isPinned: false,
                pinnedUntil: null,
                pinnedBy: null,
                pinnedByName: null,
            });
        } catch (error) {
            console.error('[useGroupChat] unpinMessage error:', error);
            throw error;
        }
    }, [groupId, user, isAdmin]);

    // ---- PINNED MESSAGES ----
    const pinnedMessages = messages.filter(m => m.isPinned && !m.isModerated);

    return {
        messages,
        pinnedMessages,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        sendMessage,
        moderateMessage,
        deleteMessage,
        pinMessage,
        unpinMessage,
        unreadCount,
        markAsRead,
        isAdmin,
        isChatLocked,
        canSendMessage,
    };
}

/**
 * Lightweight hook just for unread count — used by Navbar/Sidebar
 * without loading all messages (reads a tiny localStorage value)
 */
export function useGroupChatUnreadCount(groupId: string | undefined) {
    const { user } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        if (!groupId || !user) {
            setUnreadCount(0);
            return;
        }

        const lastReadKey = `padel_chat_lastRead_${groupId}_${user.uid}`;

        // Listen to chatMessages just for count (limit 1 desc to check last message)
        const messagesRef = collection(firestore, 'groups', groupId, 'chatMessages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(50));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const lastRead = localStorage.getItem(lastReadKey);
            let count = 0;
            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                if (data.senderId !== user.uid) {
                    if (!lastRead || data.timestamp > lastRead) {
                        count++;
                    }
                }
            });
            setUnreadCount(count);
        });

        const handleMarkRead = () => {
            setUnreadCount(0);
        };
        window.addEventListener('chat_marked_read', handleMarkRead);

        return () => {
            unsubscribe();
            window.removeEventListener('chat_marked_read', handleMarkRead);
        };
    }, [groupId, user]);

    return unreadCount;
}
